import { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@db/types/UserGameStats';
import type { UserProfile } from '@db/types/UserProfile';
import { NotFoundError, ValidationError, handleSupabaseError } from '@db/utils/errors';

/**
 * 現在のセッションからDisplay Nameを取得
 * @param supabase - Supabaseクライアント
 * @returns Display Name、取得できない場合はnull
 */
export async function getDisplayName(supabase: SupabaseClient<Database>): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    return null;
  }

  const user = session.user;
  // 優先順位：user_metadata.full_name > user_metadata.name > email
  return user.user_metadata?.full_name
    || user.user_metadata?.name
    || user.email
    || null;
}

/**
 * ユーザープロフィールを取得
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns ユーザープロフィール、存在しない場合null
 *
 * @example
 * ```typescript
 * const profile = await getUserProfile(supabase, userId);
 * if (profile) {
 *   console.log('ユーザー名:', profile.user_name);
 * }
 * ```
 */
export async function getUserProfile(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<UserProfile | null> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    handleSupabaseError(error);
  }

  return data;
}

/**
 * ユーザープロフィールを作成
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param userName - ユーザー名。指定されない場合はセッションから取得を試みる
 * @returns 作成されたプロフィール
 *
 * @example
 * ```typescript
 * const profile = await createUserProfile(supabase, userId, 'ユーザー名');
 * ```
 */
export async function createUserProfile(
  supabase: SupabaseClient<Database>,
  userId: string,
  userName?: string
): Promise<UserProfile> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  // 既存データがあるかチェック
  const existing = await getUserProfile(supabase, userId);
  if (existing) {
    return existing; // 既に存在する場合はそれを返す
  }

  // userNameが指定されていない場合は、セッションから取得を試みる
  let finalUserName: string;
  if (userName) {
    finalUserName = userName;
  } else {
    const displayName = await getDisplayName(supabase);
    finalUserName = displayName || userId; // 取得できない場合はuserIdをフォールバックとして使用
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .insert({
      user_id: userId,
      user_name: finalUserName,
    })
    .select()
    .single();

  if (error) {
    handleSupabaseError(error);
  }

  if (!data) {
    throw new NotFoundError('プロフィールの作成に失敗しました');
  }

  return data;
}

/**
 * ユーザー名を更新
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param newUserName - 新しいユーザー名
 * @returns 更新されたプロフィール
 *
 * @example
 * ```typescript
 * const profile = await updateUserName(supabase, userId, '新しいユーザー名');
 * ```
 */
export async function updateUserName(
  supabase: SupabaseClient<Database>,
  userId: string,
  newUserName: string
): Promise<UserProfile> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  if (!newUserName || typeof newUserName !== 'string') {
    throw new ValidationError('ユーザー名が指定されていません');
  }

  // プロフィールが存在しない場合は作成
  const existing = await getUserProfile(supabase, userId);
  if (!existing) {
    return await createUserProfile(supabase, userId, newUserName);
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update({ user_name: newUserName })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    handleSupabaseError(error);
  }

  if (!data) {
    throw new NotFoundError('プロフィールの更新に失敗しました');
  }

  return data;
}

/**
 * プロフィールが存在するかチェック
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns プロフィールが存在する場合true
 *
 * @example
 * ```typescript
 * if (!await profileExists(supabase, userId)) {
 *   await createUserProfile(supabase, userId);
 * }
 * ```
 */
export async function profileExists(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<boolean> {
  const profile = await getUserProfile(supabase, userId);
  return profile !== null;
}
